import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Briefcase,
  Calendar,
  UserCheck,
  UserX,
  RefreshCw,
  Shield,
  Ban,
  Crown,
  User,
  ChevronDown,
  LogOut,
  Send
} from 'lucide-react';

// Using relative URLs
const API = '/api';

const SuperAdminSignups = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingSignups, setPendingSignups] = useState([]);
  const [rejectedSignups, setRejectedSignups] = useState([]);
  const [approvedSignups, setApprovedSignups] = useState([]);
  const [suspendedSignups, setSuspendedSignups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [suspendReason, setSuspendReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [emailSending, setEmailSending] = useState({});

  useEffect(() => {
    fetchSignups();
  }, []);

  const fetchSignups = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-signups`);
      setPendingSignups(response.data.pending || []);
      setRejectedSignups(response.data.rejected || []);
      setApprovedSignups(response.data.approved || []);
      setSuspendedSignups(response.data.suspended || []);
    } catch (error) {
      toast.error('Failed to fetch signups');
      console.error('Fetch signups error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openApproveDialog = (user) => {
    setSelectedUser(user);
    setShowApproveDialog(true);
  };

  const handleApprove = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/approve-signup`, { user_id: selectedUser.id });
      toast.success(`Successfully approved ${selectedUser.email}`);
      setShowApproveDialog(false);
      setSelectedUser(null);
      fetchSignups();
    } catch (error) {
      toast.error('Failed to approve signup');
      console.error('Approve error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/reject-signup`, {
        user_id: selectedUser.id,
        reason: rejectionReason || 'Does not meet our criteria'
      });
      toast.success(`Rejected ${selectedUser.email}`);
      setShowRejectDialog(false);
      setRejectionReason('');
      setSelectedUser(null);
      fetchSignups();
    } catch (error) {
      toast.error('Failed to reject signup');
      console.error('Reject error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const openSuspendDialog = (user) => {
    setSelectedUser(user);
    setSuspendReason('');
    setShowSuspendDialog(true);
  };

  const handleSuspend = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    try {
      await axios.post(`${API}/admin/suspend-signup`, {
        user_id: selectedUser.id,
        reason: suspendReason || 'Account suspended by admin'
      });
      toast.success(`Suspended ${selectedUser.email}`);
      setShowSuspendDialog(false);
      setSuspendReason('');
      setSelectedUser(null);
      fetchSignups();
    } catch (error) {
      toast.error('Failed to suspend user');
      console.error('Suspend error:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendEmail = async (userId, emailType, userName) => {
    const key = `${userId}-${emailType}`;
    setEmailSending(prev => ({ ...prev, [key]: true }));
    
    try {
      await axios.post(`${API}/admin/resend-email`, {
        user_id: userId,
        email_type: emailType
      });
      toast.success(`${emailType.charAt(0).toUpperCase() + emailType.slice(1)} email sent to ${userName}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send email';
      toast.error(errorMsg);
      console.error('Resend email error:', error);
    } finally {
      setEmailSending(prev => ({ ...prev, [key]: false }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Signup Management</h1>
                  <p className="text-sm text-slate-600">Review and approve new signups</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={fetchSignups}
                className="border-slate-200 hover:bg-slate-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = '/login';
                    }}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Pending Approvals</p>
                  <p className="text-3xl font-bold text-orange-600">{pendingSignups.length}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Approved Users</p>
                  <p className="text-3xl font-bold text-green-600">{approvedSignups.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Rejected Signups</p>
                  <p className="text-3xl font-bold text-red-600">{rejectedSignups.length}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Suspended Users</p>
                  <p className="text-3xl font-bold text-purple-600">{suspendedSignups.length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Ban className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Signups */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-orange-600" />
            Pending Approvals ({pendingSignups.length})
          </h2>

          {pendingSignups.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <p className="text-slate-600">No pending signups</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingSignups.map((signup) => (
                <Card key={signup.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-navy-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{signup.email}</h3>
                            <p className="text-sm text-slate-600">{signup.full_name || 'No name provided'}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                          <div className="flex items-center text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 mr-2" />
                            <span>Workspace: {signup.workspace_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Signed up: {formatDate(signup.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          onClick={() => handleResendEmail(signup.id, 'welcome', signup.email)}
                          disabled={emailSending[`${signup.id}-welcome`]}
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {emailSending[`${signup.id}-welcome`] ? 'Sending...' : 'Resend Welcome'}
                        </Button>
                        <Button
                          onClick={() => openApproveDialog(signup)}
                          disabled={actionLoading}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => openRejectDialog(signup)}
                          disabled={actionLoading}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Rejected Signups */}
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <XCircle className="w-5 h-5 mr-2 text-red-600" />
            Rejected Signups ({rejectedSignups.length})
          </h2>

          {rejectedSignups.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <p className="text-slate-600">No rejected signups</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {rejectedSignups.map((signup) => (
                <Card key={signup.id} className="border-0 shadow-sm hover:shadow-md transition-shadow bg-red-50/30 overflow-hidden">
                  <CardContent className="p-6 bg-red-50/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{signup.email}</h3>
                            <p className="text-sm text-slate-600">{signup.full_name || 'No name provided'}</p>
                          </div>
                          <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                            Rejected
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                          <div className="flex items-center text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 mr-2" />
                            <span>Workspace: {signup.workspace_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Signed up: {formatDate(signup.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          onClick={() => handleResendEmail(signup.id, 'rejected', signup.email)}
                          disabled={emailSending[`${signup.id}-rejected`]}
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {emailSending[`${signup.id}-rejected`] ? 'Sending...' : 'Resend Email'}
                        </Button>
                        <Button
                          onClick={() => openApproveDialog(signup)}
                          disabled={actionLoading}
                          variant="outline"
                          className="border-green-200 text-green-600 hover:bg-green-50"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Approve Now
                        </Button>
                      </div>
                    </div>

                    {signup.rejection_reason && (
                      <div className="p-3 bg-red-100 rounded-lg">
                        <p className="text-sm font-medium text-red-900 mb-1">Rejection Reason:</p>
                        <p className="text-sm text-red-700">{signup.rejection_reason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Approved Signups */}
        <div className="mb-8 mt-12">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            Approved Signups ({approvedSignups.length})
          </h2>

          {approvedSignups.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <p className="text-slate-600">No approved signups</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {approvedSignups.map((signup) => (
                <Card key={signup.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{signup.email}</h3>
                            <p className="text-sm text-slate-600">{signup.full_name || 'No name provided'}</p>
                          </div>
                          <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                            Active
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                          <div className="flex items-center text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 mr-2" />
                            <span>Workspace: {signup.workspace_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Approved: {formatDate(signup.approved_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          onClick={() => handleResendEmail(signup.id, 'welcome', signup.email)}
                          disabled={emailSending[`${signup.id}-welcome`]}
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {emailSending[`${signup.id}-welcome`] ? 'Sending...' : 'Resend Welcome'}
                        </Button>
                        <Button
                          onClick={() => handleResendEmail(signup.id, 'approved', signup.email)}
                          disabled={emailSending[`${signup.id}-approved`]}
                          variant="outline"
                          size="sm"
                          className="border-green-200 text-green-600 hover:bg-green-50"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {emailSending[`${signup.id}-approved`] ? 'Sending...' : 'Resend Approved'}
                        </Button>
                        <Button
                          onClick={() => openRejectDialog(signup)}
                          disabled={actionLoading}
                          variant="outline"
                          className="border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <UserX className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => openSuspendDialog(signup)}
                          disabled={actionLoading}
                          variant="outline"
                          className="border-purple-200 text-purple-600 hover:bg-purple-50"
                        >
                          <Ban className="w-4 h-4 mr-2" />
                          Suspend
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Suspended Signups */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center">
            <Ban className="w-5 h-5 mr-2 text-purple-600" />
            Suspended Users ({suspendedSignups.length})
          </h2>

          {suspendedSignups.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <p className="text-slate-600">No suspended users</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {suspendedSignups.map((signup) => (
                <Card key={signup.id} className="border-0 shadow-sm hover:shadow-md transition-shadow bg-purple-50/30 overflow-hidden">
                  <CardContent className="p-6 bg-purple-50/30">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{signup.email}</h3>
                            <p className="text-sm text-slate-600">{signup.full_name || 'No name provided'}</p>
                          </div>
                          <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-200">
                            Suspended
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-13">
                          <div className="flex items-center text-sm text-slate-600">
                            <Briefcase className="w-4 h-4 mr-2" />
                            <span>Workspace: {signup.workspace_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center text-sm text-slate-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Signed up: {formatDate(signup.created_at)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <Button
                          onClick={() => handleResendEmail(signup.id, 'suspended', signup.email)}
                          disabled={emailSending[`${signup.id}-suspended`]}
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        >
                          <Send className="w-3 h-3 mr-1" />
                          {emailSending[`${signup.id}-suspended`] ? 'Sending...' : 'Resend Email'}
                        </Button>
                        <Button
                          onClick={() => openApproveDialog(signup)}
                          disabled={actionLoading}
                          variant="outline"
                          className="border-green-200 text-green-600 hover:bg-green-50"
                        >
                          <UserCheck className="w-4 h-4 mr-2" />
                          Reactivate
                        </Button>
                      </div>
                    </div>

                    {signup.rejection_reason && (
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <p className="text-sm font-medium text-purple-900 mb-1">Suspension Reason:</p>
                        <p className="text-sm text-purple-700">{signup.rejection_reason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Signup</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve this user?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Email:</span>
                  <span className="text-sm font-medium text-slate-900">{selectedUser?.email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Name:</span>
                  <span className="text-sm font-medium text-slate-900">{selectedUser?.full_name || 'Not provided'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Workspace:</span>
                  <span className="text-sm font-medium text-slate-900">{selectedUser?.workspace_name || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">This user will be able to:</p>
                  <ul className="text-sm text-green-700 mt-2 space-y-1 ml-4 list-disc">
                    <li>Log in to their account immediately</li>
                    <li>Access all platform features</li>
                    <li>Start compliance certification process</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowApproveDialog(false);
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {actionLoading ? 'Approving...' : 'Approve Signup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Signup</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="reason">Rejection Reason</Label>
              <Textarea
                id="reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Invalid business email, suspicious activity, etc."
                className="mt-2 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason('');
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {actionLoading ? 'Rejecting...' : 'Reject Signup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend User</DialogTitle>
            <DialogDescription>
              Please provide a reason for suspending {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="suspend-reason">Suspension Reason</Label>
              <Textarea
                id="suspend-reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g., Policy violation, suspicious activity, etc."
                className="mt-2 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowSuspendDialog(false);
                setSuspendReason('');
                setSelectedUser(null);
              }}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSuspend}
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {actionLoading ? 'Suspending...' : 'Suspend User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminSignups;
